import { type SubscriptionStatus } from "../../../payment/plans";
import { useQuery, getPaginatedUsers } from "wasp/client/operations";
import { useState, useEffect } from "react";
import { UserFlairBadge } from "../../../components/common";
import { Input, Select, SurfaceCard } from "../../../components/ui";
import type { UserFlair } from "../../../shared/userFlair";
import LoadingSpinner from "../../layout/LoadingSpinner";
import { AdminPaginationControl } from "../AdminPaginationControl";
import { ADMIN_DASHBOARD_PAGE_SIZE } from "../adminControlContracts";
import { IsAdminToggle } from "./IsAdminToggle";
import { cn } from "../../../client/cn";

// --- Filter option constants ---

const SUBSCRIPTION_FILTER_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "cancel_at_period_end", label: "Canceled" },
  { value: "paid_once", label: "Paid Once" },
  { value: "past_due", label: "Past Due" },
  { value: "deleted", label: "Deleted" },
  { value: "none", label: "No subscription" },
] as const;

const ADMIN_FILTER_OPTIONS = [
  { value: "both", label: "All" },
  { value: "true", label: "Admin only" },
  { value: "false", label: "Non-admin" },
] as const;

type SubscriptionFilterValue =
  (typeof SUBSCRIPTION_FILTER_OPTIONS)[number]["value"];

// --- Helpers ---

function getInitials(email: string): string {
  const local = email.split("@")[0] ?? "";
  return local.slice(0, 2).toUpperCase() || "??";
}

function getAvatarColor(email: string): string {
  const colors = [
    "bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300",
    "bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-300",
    "bg-warning-100 text-warning-700 dark:bg-warning-900/40 dark:text-warning-300",
    "bg-info-100 text-info-700 dark:bg-info-900/40 dark:text-info-300",
  ];
  const idx = email.charCodeAt(0) % colors.length;
  return colors[idx]!;
}

const subscriptionBadgeClass: Record<string, string> = {
  active:
    "bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400",
  cancel_at_period_end:
    "bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400",
  past_due:
    "bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400",
  paid_once: "bg-info-100 text-info-700 dark:bg-info-900/30 dark:text-info-400",
  deleted:
    "bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400",
  none: "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400",
};

const subscriptionBadgeLabel: Record<string, string> = {
  active: "Active",
  cancel_at_period_end: "Canceled",
  past_due: "Past Due",
  paid_once: "Paid Once",
  deleted: "Deleted",
  none: "None",
};

function SubscriptionBadge({ status }: { status: string | null | undefined }) {
  const key = status ?? "none";
  const badgeClass =
    subscriptionBadgeClass[key] ?? subscriptionBadgeClass["none"]!;
  const label = subscriptionBadgeLabel[key] ?? key;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium leading-none",
        badgeClass,
      )}
    >
      {label}
    </span>
  );
}

function UserAvatar({ email }: { email: string }) {
  return (
    <div
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
        getAvatarColor(email),
      )}
    >
      {getInitials(email)}
    </div>
  );
}

function StripeId({ id }: { id: string | null | undefined }) {
  if (!id)
    return <span className="text-neutral-400 dark:text-neutral-600">—</span>;
  const truncated = id.length > 18 ? `${id.slice(0, 18)}…` : id;
  return (
    <span
      className="font-mono text-xs text-neutral-700 dark:text-neutral-300"
      title={id}
    >
      {truncated}
    </span>
  );
}

// --- Column header class ---

const COL_HEADER =
  "text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400";
const MOBILE_LABEL =
  "text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400";

// --- Main component ---

// Users table reuses admin contracts and shared pagination so filter/layout behavior matches the scoped Wave 8 refactor. (DL-001, DL-003)
const UsersTable = () => {
  const [skip, setSkip] = useState(0);
  const [page, setPage] = useState(1);
  const [email, setEmail] = useState("");
  const [isAdminFilter, setIsAdminFilter] = useState<boolean | undefined>(
    undefined,
  );
  const [statusFilter, setStatusFilter] =
    useState<SubscriptionFilterValue>("all");

  const subscriptionStatusFilter =
    statusFilter === "all"
      ? undefined
      : statusFilter === "none"
        ? // null sentinel: server treats [null] as "subscriptionStatus IS NULL" filter
          ([null] as unknown as SubscriptionStatus[])
        : ([statusFilter] as SubscriptionStatus[]);

  const { data, isLoading, error } = useQuery(getPaginatedUsers, {
    skip,
    emailContains: email || undefined,
    isAdmin: isAdminFilter,
    subscriptionStatus: subscriptionStatusFilter,
  });

  const totalPages = data?.totalPages || 1;
  const isAdminFilterValue =
    isAdminFilter === undefined ? "both" : isAdminFilter ? "true" : "false";
  const users = data?.users ?? [];
  const hasUsers = users.length > 0;

  useEffect(() => {
    setPage(1);
  }, [email, statusFilter, isAdminFilter]);

  useEffect(() => {
    setSkip((page - 1) * ADMIN_DASHBOARD_PAGE_SIZE);
  }, [page, setSkip]);

  if (error) {
    return (
      <SurfaceCard>
        <p className="text-danger-600">Failed to load users: {error.message}</p>
      </SurfaceCard>
    );
  }

  return (
    <SurfaceCard padding="none">
      {/* Filter header */}
      <div className="flex flex-col items-start gap-4 bg-neutral-100/40 p-4 dark:bg-neutral-700/50 sm:p-6">
        <span className="text-sm font-medium">Filters</span>
        <div className="flex w-full flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="grid w-full gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <Input
              type="text"
              id="email-filter"
              label="Email"
              placeholder="dude@example.com"
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
              className="h-10 rounded-sm"
            />
            <Select
              label="Subscription"
              value={statusFilter}
              onChange={(value) =>
                setStatusFilter(value as SubscriptionFilterValue)
              }
              options={SUBSCRIPTION_FILTER_OPTIONS}
              className="h-10 w-full min-w-0 rounded-sm"
            />
            <Select
              label="Admin access"
              value={isAdminFilterValue}
              onChange={(value) => {
                if (value === "both") {
                  setIsAdminFilter(undefined);
                } else {
                  setIsAdminFilter(value === "true");
                }
              }}
              options={ADMIN_FILTER_OPTIONS}
              className="h-10 w-full min-w-0 rounded-sm"
            />
          </div>
          {!isLoading && (
            <div className="w-full xl:w-auto">
              <AdminPaginationControl
                page={page}
                totalPages={totalPages}
                onPageChange={(nextPage) => setPage(nextPage)}
                className="justify-start xl:justify-end"
              />
            </div>
          )}
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="py-8">
          <LoadingSpinner />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !hasUsers && (
        <div className="border-t border-stroke px-4 py-8 text-center dark:border-strokedark md:px-6">
          <p className="text-neutral-600 dark:text-neutral-400">
            No users found matching the current filters.
          </p>
        </div>
      )}

      {!isLoading && hasUsers && (
        <>
          {/* Desktop column header */}
          <div className="hidden border-t border-stroke px-4 py-3 dark:border-strokedark md:grid md:grid-cols-12 md:px-6">
            <div className="col-span-5 flex items-center">
              <p className={COL_HEADER}>User</p>
            </div>
            <div className="col-span-2 flex items-center">
              <p className={COL_HEADER}>Last Active</p>
            </div>
            <div className="col-span-2 flex items-center">
              <p className={COL_HEADER}>Subscription</p>
            </div>
            <div className="col-span-2 flex items-center">
              <p className={COL_HEADER}>Stripe ID</p>
            </div>
            <div className="col-span-1 flex items-center">
              <p className={COL_HEADER}>Admin</p>
            </div>
          </div>

          {/* Unified responsive rows */}
          <div>
            {users.map((user) => {
              const userFlairTier = user.flair?.tier;

              return (
                <div
                  key={user.id}
                  className="grid grid-cols-1 gap-3 border-t border-stroke px-4 py-4 transition-colors hover:bg-neutral-100/30 dark:border-strokedark dark:hover:bg-neutral-800/30 md:grid-cols-12 md:gap-4 md:px-6"
                >
                  {/* User */}
                  <div className="flex items-start gap-3 md:col-span-5 md:items-center">
                    <UserAvatar email={user.email ?? ""} />
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <p className="truncate text-sm font-semibold text-black dark:text-white">
                        {user.email}
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                          {user.username}
                        </p>
                        <UserFlairBadge
                          flair={userFlairTier}
                          variant="compact"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Last Active */}
                  <div className="flex flex-col gap-0.5 md:col-span-2 md:justify-center">
                    <p className={`${MOBILE_LABEL} md:hidden`}>Last Active</p>
                    <p className="text-sm text-black dark:text-white">
                      {user.lastActiveTimestamp.toLocaleDateString()}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      {user.lastActiveTimestamp.toLocaleTimeString()}
                    </p>
                  </div>

                  {/* Subscription */}
                  <div className="flex flex-col items-start gap-1 md:col-span-2 md:justify-center">
                    <p className={`${MOBILE_LABEL} md:hidden`}>Subscription</p>
                    <SubscriptionBadge status={user.subscriptionStatus} />
                  </div>

                  {/* Stripe ID */}
                  <div className="flex flex-col gap-1 md:col-span-2 md:justify-center">
                    <p className={`${MOBILE_LABEL} md:hidden`}>Stripe ID</p>
                    <StripeId id={user.paymentProcessorUserId} />
                  </div>

                  {/* Admin */}
                  <div className="flex flex-col gap-1 md:col-span-1 md:justify-center">
                    <p className={`${MOBILE_LABEL} md:hidden`}>Admin</p>
                    <IsAdminToggle userId={user.id} isAdmin={user.isAdmin} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="border-t border-stroke px-4 py-3 dark:border-strokedark md:px-6">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Showing {users.length} of {data?.totalCount ?? users.length} users
            </p>
          </div>
        </>
      )}
    </SurfaceCard>
  );
};

export default UsersTable;
